import { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import Loader from 'shared/Loader';

import { isBootstrapQuerySettled, useAuthBootstrapStatus } from 'contexts/authBootstrapContext';
import { useMyPrograms } from 'hooks/useMyPrograms/useMyPrograms';
import { PathName } from 'router/paths';

type ProgramType = 'WM' | 'MH' | 'WH' | 'TRT';

interface RequireProgramProps {
  children?: ReactNode;
  fallback?: ReactNode;
  program: ProgramType;
}

const RequireProgram = ({ children, fallback, program }: RequireProgramProps) => {
  const { mensHealth, mentalHealth, weightManagement, womensHealth } = useMyPrograms();
  const { myPrograms } = useAuthBootstrapStatus();

  const programMap: Record<ProgramType, unknown> = {
    MH: mentalHealth,
    TRT: mensHealth,
    WH: womensHealth,
    WM: weightManagement
  };

  if (!programMap[program]) {
    // The programs slice is empty both before /my-programs resolves and when the
    // patient genuinely has no such program, so an absent program on its own is
    // not an answer yet. Treating it as one is how a post-checkout deep link
    // (e.g. /wh-post-onboarding) lost its destination and bounced to Home.
    //
    // This reads the status of the argless `getMyPrograms()` that App.tsx's
    // bootstrap effect fires whenever accessToken is truthy, and keeps alive via
    // useLazyGetMyProgramsQuery for the app's lifetime. Call it with an argument
    // and you create a different cache entry that this will not observe.
    if (!isBootstrapQuerySettled(myPrograms)) {
      return <Loader isVisible />;
    }

    return <>{fallback ?? <Navigate to={PathName.NotFound} replace />}</>;
  }

  return <>{children ?? <Outlet />}</>;
};

export default RequireProgram;
